import React, { useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  CreditCard,
  Crown,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Building2,
  User,
  Database,
  Phone,
  Mail,
  RefreshCw,
  AlertTriangle,
  MessageCircle,
  Headphones,
  Copy,
  ExternalLink
} from 'lucide-react'
import { formatDate, getDaysRemaining } from '@/lib/utils'

const SUPPORT_PHONE = '+91 8891632568'

export default function SubscriptionScreen() {
  const { user } = useAuth()
  const { currentRestaurant } = useRestaurant()
  const [showContactModal, setShowContactModal] = useState(false)
  const [copied, setCopied] = useState(false)

  const loadSubscriptionDetails = async () => {
    // We get subscription details from user context, no need for separate API call
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCallSupport = () => {
    window.location.href = `tel:${SUPPORT_PHONE}`
  }

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hi, I need help with my subscription renewal.\n\nRestaurant: ${currentRestaurant?.name || user?.name}\nEmail: ${user?.email}\nCurrent Plan: Annual\nExpiry: ${user?.subscriptionEndDate ? formatDate(user.subscriptionEndDate) : 'N/A'}`
    )
    window.open(`https://wa.me/918891632568?text=${message}`, '_blank')
  }

  const getSubscriptionStatusBadge = () => {
    if (!user) return null

    const daysRemaining = user.subscriptionEndDate ? getDaysRemaining(new Date(user.subscriptionEndDate)) : 0
    const subscriptionStatus = user.subscriptionStatus || 'active'

    const isActiveByDate = daysRemaining > 0
    const isActiveByStatus = subscriptionStatus === 'active'
    const isActuallyActive = isActiveByStatus && isActiveByDate

    if (subscriptionStatus === 'suspended') {
      return (
        <Badge variant="secondary" className="text-sm">
          <AlertTriangle className="w-4 h-4 mr-2" />
          Suspended
        </Badge>
      )
    } else if (!isActuallyActive) {
      return (
        <Badge variant="destructive" className="text-sm">
          <XCircle className="w-4 h-4 mr-2" />
          Expired
        </Badge>
      )
    } else if (daysRemaining <= 7) {
      return (
        <Badge className="bg-red-500 hover:bg-red-600 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" />
          Expires in {daysRemaining} days
        </Badge>
      )
    } else if (daysRemaining <= 30) {
      return (
        <Badge className="bg-orange-500 hover:bg-orange-600 text-sm">
          <AlertCircle className="w-4 h-4 mr-2" />
          Expires in {daysRemaining} days
        </Badge>
      )
    } else {
      return (
        <Badge className="bg-green-500 hover:bg-green-600 text-sm">
          <CheckCircle className="w-4 h-4 mr-2" />
          Active ({daysRemaining} days)
        </Badge>
      )
    }
  }

  const getSubscriptionProgress = () => {
    if (!user?.subscriptionStartDate || !user?.subscriptionEndDate) return 0

    const startDate = new Date(user.subscriptionStartDate)
    const endDate = new Date(user.subscriptionEndDate)
    const currentDate = new Date()

    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    const elapsedDays = Math.ceil((currentDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))

    return Math.min(Math.max((elapsedDays / totalDays) * 100, 0), 100)
  }

  const daysRemaining = user?.subscriptionEndDate ? getDaysRemaining(new Date(user.subscriptionEndDate)) : 0
  const subscriptionStatus = user?.subscriptionStatus || 'active'
  const progressValue = getSubscriptionProgress()

  const isActiveByDate = daysRemaining > 0
  const isActiveByStatus = subscriptionStatus === 'active'
  const isActuallyActive = isActiveByStatus && isActiveByDate

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">Subscription</h1>
          <p className="text-muted-foreground text-sm sm:text-base truncate">
            Your subscription
          </p>
        </div>
        <Button onClick={loadSubscriptionDetails} variant="outline" size="icon" className="py-[10px] sm:w-auto sm:h-auto sm:px-4 flex-shrink-0">
          <RefreshCw className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Subscription Status Alert */}
      {!isActuallyActive && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Subscription Issue:</strong> Your subscription is currently {daysRemaining <= 0 ? 'expired' : subscriptionStatus}.
            Some features may be limited. Please contact support for assistance.
          </AlertDescription>
        </Alert>
      )}

      {daysRemaining <= 30 && daysRemaining > 0 && isActuallyActive && (
        <Alert className="border-orange-200 bg-orange-50">
          <AlertCircle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>Renewal Reminder:</strong> Your subscription expires in {daysRemaining} days.
            Contact support to renew and avoid service interruption.
          </AlertDescription>
        </Alert>
      )}

      {/* Current Subscription Status */}
      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <Crown className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg sm:text-xl">Current Plan</CardTitle>
            </div>
            {getSubscriptionStatusBadge()}
          </div>
          <CardDescription className="text-xs sm:text-sm">
            Your subscription details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* Mobile-optimized Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5 p-3 sm:p-4 rounded-lg bg-accent/50">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <span className="text-xs font-medium">Plan</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold">Standard</div>
              <div className="text-xs text-muted-foreground">Annual</div>
            </div>

            <div className="space-y-1.5 p-3 sm:p-4 rounded-lg bg-accent/50">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <span className="text-xs font-medium">Days Left</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold">{daysRemaining}</div>
              <div className="text-xs text-muted-foreground">Remaining</div>
            </div>

            <div className="space-y-1.5 p-3 sm:p-4 rounded-lg bg-accent/50 col-span-2 sm:col-span-1">
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground" />
                <span className="text-xs font-medium">Value</span>
              </div>
              <div className="text-lg sm:text-2xl font-bold">₹{(user?.subscriptionAmount || currentRestaurant?.subscriptionAmount || 12000).toLocaleString('en-IN')}</div>
              <div className="text-xs text-muted-foreground">Per year</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="font-medium">Usage Progress</span>
              <span className="text-muted-foreground">{Math.round(progressValue)}%</span>
            </div>
            <Progress value={progressValue} className="h-2.5 sm:h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{user?.subscriptionStartDate ? formatDate(user.subscriptionStartDate) : 'N/A'}</span>
              <span>{user?.subscriptionEndDate ? formatDate(user.subscriptionEndDate) : 'N/A'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subscription Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Account Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
              <User className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Account Info</span>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Your account details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div className="space-y-3">
              <div className="flex items-start space-x-3 p-2.5 rounded-lg bg-accent/30">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">Account Owner</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-2.5 rounded-lg bg-accent/30">
                <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{user?.email}</div>
                  <div className="text-xs text-muted-foreground">Primary Email</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-2.5 rounded-lg bg-accent/30">
                <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{user?.phone || 'Not provided'}</div>
                  <div className="text-xs text-muted-foreground">Contact Number</div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-2.5 rounded-lg bg-accent/30">
                <Database className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium font-mono text-xs sm:text-sm truncate">{user?.databaseName}</div>
                  <div className="text-xs text-muted-foreground">Database Instance</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
              <CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Billing Info</span>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Payment & billing details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4">
            <div className="space-y-2.5 sm:space-y-3">
              <div className="flex justify-between items-center p-2.5 rounded-lg bg-accent/30">
                <span className="text-xs sm:text-sm font-medium">Payment</span>
                <Badge variant={user?.paymentStatus === 'paid' ? 'default' : 'destructive'} className="text-xs">
                  {user?.paymentStatus || 'Unknown'}
                </Badge>
              </div>

              <div className="flex justify-between items-center p-2.5 rounded-lg bg-accent/30">
                <span className="text-xs sm:text-sm font-medium">Status</span>
                <Badge variant={isActuallyActive ? 'default' : 'destructive'} className="text-xs">
                  {isActuallyActive ? 'active' : (daysRemaining <= 0 ? 'expired' : subscriptionStatus)}
                </Badge>
              </div>

              <div className="flex justify-between items-center p-2.5 rounded-lg bg-accent/30">
                <span className="text-xs sm:text-sm font-medium">Plan Type</span>
                <span className="text-xs sm:text-sm">Annual</span>
              </div>

              <div className="flex justify-between items-center p-2.5 rounded-lg bg-accent/30">
                <span className="text-xs sm:text-sm font-medium">Amount</span>
                <span className="text-sm font-semibold">₹{(user?.subscriptionAmount || currentRestaurant?.subscriptionAmount || 12000).toLocaleString('en-IN')}</span>
              </div>

              <div className="flex justify-between items-center p-2.5 rounded-lg bg-accent/30">
                <span className="text-xs sm:text-sm font-medium">Renewal</span>
                <span className="text-xs sm:text-sm truncate ml-2">{user?.subscriptionEndDate ? formatDate(user.subscriptionEndDate) : 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Features & Support */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Features Included */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              <span>Features</span>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Included in your plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 sm:space-y-2.5">
              {[
                'Dashboard & Analytics',
                'Sales Reports & Export',
                'Profit Analysis',
                'Order Management',
                'Real-time Notifications',
                'Data Backup & Security',
                'Customer Support',
                'Regular Updates'
              ].map((feature, index) => (
                <div key={index} className="flex items-center space-x-2.5 p-2 rounded-lg hover:bg-accent/30 transition-colors">
                  <CheckCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Support & Actions */}
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-3 bg-gradient-to-r from-primary/5 to-primary/10">
            <CardTitle className="flex items-center space-x-2 text-base sm:text-lg">
              <Headphones className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span>Support & Renewal</span>
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Get help with your subscription
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            {/* Main CTA Button */}
            <Button
              onClick={() => setShowContactModal(true)}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full"
              size="lg"
            >
              <Phone className="h-5 w-5 mr-3" />
              Contact Support for Renewal
            </Button>

            {/* Quick Contact Info */}
            <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-500 rounded-full">
                    <Phone className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Customer Care</p>
                    <p className="text-lg font-bold text-green-700 dark:text-green-400">{SUPPORT_PHONE}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCallSupport}
                  className="border-green-300 text-green-700 hover:bg-green-100 dark:border-green-700 dark:text-green-400"
                >
                  <Phone className="h-4 w-4 mr-1" />
                  Call
                </Button>
              </div>
            </div>

            {/* Help Topics */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: RefreshCw, label: 'Renewal' },
                { icon: CreditCard, label: 'Payment' },
                { icon: Crown, label: 'Upgrade' },
                { icon: Headphones, label: 'Support' }
              ].map((item, index) => (
                <button
                  key={index}
                  onClick={() => setShowContactModal(true)}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors text-left"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contact Support Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent className="max-w-md">
          <DialogHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mb-4">
              <Headphones className="h-8 w-8 text-white" />
            </div>
            <DialogTitle className="text-xl">Contact Support</DialogTitle>
            <DialogDescription>
              Reach out to our support team for subscription renewal and payment assistance
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Phone Number Card */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20">
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/20 rounded-full">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Customer Care Number</p>
                  <p className="text-2xl font-bold tracking-wide">{SUPPORT_PHONE}</p>
                </div>
                <div className="flex gap-2 justify-center pt-2">
                  <Button onClick={handleCallSupport} className="flex-1">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(SUPPORT_PHONE)}
                    className="px-4"
                  >
                    {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>

            {/* WhatsApp Option */}
            <Button
              onClick={handleWhatsApp}
              variant="outline"
              className="w-full h-12 border-2 border-green-500 text-green-600 hover:bg-green-50 hover:text-green-700"
            >
              <MessageCircle className="h-5 w-5 mr-2 fill-green-500" />
              Message on WhatsApp
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>

            {/* Info Section */}
            <div className="p-4 rounded-lg bg-accent/50 space-y-3">
              <p className="text-sm font-medium">Our support team can help you with:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  'Subscription Renewal',
                  'Payment Options',
                  'Plan Upgrades',
                  'Technical Issues',
                  'Billing Queries',
                  'Account Support'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Working Hours */}
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Available Mon-Sat, 9 AM - 7 PM</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
